// Cuisine + vibe tags. Preset ids are what gets stored (restaurant.cuisine / restaurant.vibe, ≤ 3 each).
// suggestCategories() guesses from Google Places `types` first, then from words in the name.

export const MAX_TAGS = 3;

export const CUISINE = [
  { id: 'mexican', emoji: '🌮', label: 'Mexican', types: ['mexican_restaurant'], words: ['taco', 'taqueria', 'mexican', 'cantina', 'burrito', 'birria', 'carniceria', 'tortas', 'mariscos', 'pozole', 'elote'] },
  { id: 'italian', emoji: '🍝', label: 'Italian', types: ['italian_restaurant'], words: ['italian', 'trattoria', 'osteria', 'ristorante', 'pasta', 'cucina', 'pizzeria'] },
  { id: 'pizza', emoji: '🍕', label: 'Pizza', types: ['pizza_restaurant'], words: ['pizza', 'pizzeria', 'slice'] },
  { id: 'sushi', emoji: '🍣', label: 'Sushi', types: ['sushi_restaurant', 'japanese_restaurant'], words: ['sushi', 'omakase', 'izakaya', 'japanese', 'sashimi'] },
  { id: 'ramen', emoji: '🍜', label: 'Ramen', types: ['ramen_restaurant'], words: ['ramen', 'noodle', 'noodles', 'pho', 'udon'] },
  { id: 'chinese', emoji: '🥡', label: 'Chinese', types: ['chinese_restaurant'], words: ['chinese', 'dim sum', 'szechuan', 'sichuan', 'dumpling', 'wok', 'cantonese', 'hot pot'] },
  { id: 'thai', emoji: '🍛', label: 'Thai', types: ['thai_restaurant'], words: ['thai', 'pad thai', 'bangkok'] },
  { id: 'indian', emoji: '🫓', label: 'Indian', types: ['indian_restaurant'], words: ['indian', 'curry', 'tandoor', 'masala', 'biryani', 'dosa'] },
  { id: 'korean', emoji: '🥘', label: 'Korean', types: ['korean_restaurant'], words: ['korean', 'kbbq', 'bibimbap', 'bulgogi', 'kimchi'] },
  { id: 'bbq', emoji: '🍖', label: 'BBQ', types: ['barbecue_restaurant'], words: ['bbq', 'barbecue', 'barbeque', 'smokehouse', 'brisket'] },
  { id: 'burgers', emoji: '🍔', label: 'Burgers', types: ['hamburger_restaurant', 'fast_food_restaurant'], words: ['burger', 'burgers', 'smash'] },
  { id: 'steak', emoji: '🥩', label: 'Steak', types: ['steak_house'], words: ['steak', 'steakhouse', 'chophouse', 'prime'] },
  { id: 'seafood', emoji: '🦞', label: 'Seafood', types: ['seafood_restaurant'], words: ['seafood', 'oyster', 'oysters', 'fish', 'crab', 'lobster', 'shrimp'] },
  { id: 'mediterranean', emoji: '🥙', label: 'Mediterranean', types: ['mediterranean_restaurant', 'greek_restaurant', 'middle_eastern_restaurant', 'turkish_restaurant', 'lebanese_restaurant'], words: ['mediterranean', 'greek', 'gyro', 'gyros', 'falafel', 'shawarma', 'kebab', 'hummus', 'lebanese', 'turkish'] },
  { id: 'american', emoji: '🍟', label: 'American', types: ['american_restaurant', 'diner'], words: ['diner', 'grill', 'tavern', 'kitchen', 'american'] },
  { id: 'breakfast', emoji: '🥞', label: 'Breakfast', types: ['breakfast_restaurant', 'brunch_restaurant'], words: ['breakfast', 'brunch', 'pancake', 'pancakes', 'waffle', 'eggs'] },
  { id: 'cafe', emoji: '☕', label: 'Cafe', types: ['cafe', 'coffee_shop', 'bakery'], words: ['cafe', 'café', 'coffee', 'espresso', 'bakery', 'patisserie', 'bagel'] },
  { id: 'bar', emoji: '🍺', label: 'Bar', types: ['bar', 'pub', 'wine_bar', 'brewery'], words: ['bar', 'pub', 'brewery', 'brewing', 'taproom', 'lounge', 'saloon', 'cocktail'] },
  { id: 'dessert', emoji: '🍰', label: 'Dessert', types: ['ice_cream_shop', 'dessert_shop', 'dessert_restaurant', 'candy_store', 'chocolate_shop'], words: ['ice cream', 'gelato', 'dessert', 'donut', 'doughnut', 'cake', 'cookie', 'churro', 'paleteria', 'sweet'] },
  { id: 'vegan', emoji: '🥗', label: 'Vegan', types: ['vegan_restaurant', 'vegetarian_restaurant'], words: ['vegan', 'vegetarian', 'plant'] },
];

export const VIBE = [
  { id: 'date-night', emoji: '🕯️', label: 'Date night', types: ['fine_dining_restaurant'], words: [] },
  { id: 'brunch', emoji: '🥂', label: 'Brunch', types: ['breakfast_restaurant', 'brunch_restaurant', 'cafe', 'bakery', 'coffee_shop'], words: ['brunch', 'breakfast'] },
  { id: 'casual', emoji: '🍺', label: 'Casual', types: ['bar', 'pub', 'fast_food_restaurant', 'meal_takeaway', 'brewery'], words: ['taproom', 'pub'] },
  { id: 'fancy', emoji: '🍷', label: 'Fancy', types: ['fine_dining_restaurant'], words: ['fine dining', 'steakhouse', 'omakase'] },
  { id: 'veggie', emoji: '🌿', label: 'Veggie-friendly', types: ['vegan_restaurant', 'vegetarian_restaurant'], words: ['vegan', 'vegetarian'] },
  { id: 'dessert', emoji: '🍨', label: 'Dessert run', types: ['ice_cream_shop', 'dessert_shop', 'dessert_restaurant'], words: ['ice cream', 'gelato', 'dessert'] },
  { id: 'patio', emoji: '☀️', label: 'Patio', types: [], words: ['patio', 'garden', 'rooftop'] },
  { id: 'late-night', emoji: '🌙', label: 'Late night', types: ['night_club'], words: ['late night', 'after hours', '24'] },
];

const byId = (list) => Object.fromEntries(list.map((p) => [p.id, p]));
export const CUISINE_BY_ID = byId(CUISINE);
export const VIBE_BY_ID = byId(VIBE);

export function cuisineLabel(id) {
  const p = CUISINE_BY_ID[id];
  return p ? `${p.emoji} ${p.label}` : id;
}

export function vibeLabel(id) {
  const p = VIBE_BY_ID[id];
  return p ? `${p.emoji} ${p.label}` : id;
}

function wordsIn(name) {
  return ` ${String(name || '').toLowerCase().replace(/[^a-z0-9à-ÿ&' ]+/g, ' ').replace(/\s+/g, ' ').trim()} `;
}

function matchList(list, name, types) {
  const found = [];
  const typeSet = new Set((types || []).map((t) => String(t).toLowerCase()));
  const padded = wordsIn(name);
  for (const p of list) {
    const byType = p.types.some((t) => typeSet.has(t));
    const byWord = p.words.some((w) => padded.includes(` ${w} `) || (w.length > 4 && padded.includes(w)));
    if (byType || byWord) found.push({ id: p.id, score: (byType ? 2 : 0) + (byWord ? 1 : 0), byType });
  }
  // Generic words ('bar', 'cafe', 'grill'...) only count when nothing more specific matched
  const GENERIC = new Set(['bar', 'cafe', 'american']);
  if (found.some((f) => !GENERIC.has(f.id))) {
    for (let i = found.length - 1; i >= 0; i--) if (GENERIC.has(found[i].id) && !found[i].byType) found.splice(i, 1);
  }
  found.sort((a, b) => b.score - a.score);
  return found.map((f) => f.id);
}

/**
 * Suggest tags from a place name and Google Places types.
 * Returns { cuisine: [...ids], vibe: [...ids] }, each capped at MAX_TAGS, best matches first.
 * Generic Google types ('restaurant', 'food', 'point_of_interest', 'establishment') carry no signal and are ignored.
 */
export function suggestCategories(name, types = []) {
  const cuisine = matchList(CUISINE, name, types);
  const vibe = matchList(VIBE, name, types);
  // A cuisine hit that implies a vibe: cafe/breakfast → brunch, bar → casual, dessert → dessert run, vegan → veggie
  const implied = { cafe: 'brunch', breakfast: 'brunch', bar: 'casual', dessert: 'dessert', vegan: 'veggie', steak: 'fancy' };
  for (const c of cuisine) if (implied[c] && !vibe.includes(implied[c])) vibe.push(implied[c]);
  return { cuisine: cuisine.slice(0, MAX_TAGS), vibe: vibe.slice(0, MAX_TAGS) };
}

/** Normalise a stored tag list: known ids only, unique, capped. */
export function cleanTags(list, table) {
  const out = [];
  for (const id of Array.isArray(list) ? list : []) if (table[id] && !out.includes(id)) out.push(id);
  return out.slice(0, MAX_TAGS);
}
